import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useLocation } from "wouter";
import {
  Check,
  CheckCircle2,
  Loader2,
  RotateCw,
  ShieldAlert,
  ShieldCheck,
  Store,
} from "lucide-react";
import { Flag, Logo } from "@/components/bits";
import { ReceiptTicket } from "@/components/receipt-ticket";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiRequest } from "@/services/api-client";
import { countryOf } from "@/types/dashboard";
import { isGenericPaymentMethod } from "@/lib/service-catalog";
import { useI18n, LanguageSwitcher } from "@/i18n/i18n-context";
import {
  ErrorBox,
  fmtAmount,
  fmtWhen,
  isAbort,
  pick,
  record,
  rows,
  text,
  type ApiRecord,
} from "@/features/finance/kit";

function idempotencyKey() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `checkout-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

interface CheckoutOperator {
  code: string;
  label: string;
  payment_method: string;
  otp_required: boolean;
}

interface CheckoutCountry {
  code: string;
  name: string;
  currency: string;
  payable_amount: string;
  exchange_rate: string;
  operators: CheckoutOperator[];
  payment_methods: Array<{
    code: string;
    label: string;
    otp_required?: boolean;
  }>;
}

/** Public hosted checkout. It never reads or sends a dashboard token. */
export default function CheckoutPage({
  code: codeProp,
}: { code?: string } = {}) {
  const { t } = useI18n();
  const [location] = useLocation();
  const match =
    location.match(/^\/payment-links\/([^/]+)\/checkout\/?$/) ??
    location.match(/^\/link\/([^/]+)\/?$/);
  const code = codeProp ?? (match ? decodeURIComponent(match[1]) : "");
  const [data, setData] = useState<ApiRecord | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedOperator, setSelectedOperator] = useState("");
  const [phone, setPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ApiRecord | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string>("PROCESSING");
  const [providerRef, setProviderRef] = useState<string>("");
  const [pollingBusy, setPollingBusy] = useState(false);

  const paymentRef = text(result, "payment_reference", "reference", "id");

  async function checkStatusNow() {
    if (!paymentRef || !code || pollingBusy) return;
    setPollingBusy(true);
    try {
      const { data: payload } = await apiRequest<ApiRecord>(
        "public",
        `/payment-links/${encodeURIComponent(code)}/checkout/status/${encodeURIComponent(paymentRef)}`,
        { authenticated: false },
      );
      const rec = record(payload);
      setResult((current) => (current ? { ...current, ...rec } : rec));
      const st = text(rec, "status").toUpperCase();
      if (st) setPaymentStatus(st);
      if (text(rec, "provider_reference")) {
        setProviderRef(text(rec, "provider_reference"));
      }
      if (st === "FAILED" && text(rec, "failure_url")) {
        setTimeout(() => {
          window.location.href = text(rec, "failure_url");
        }, 2500);
      }
    } catch {
      // Ignored
    } finally {
      setPollingBusy(false);
    }
  }

  useEffect(() => {
    if (
      !paymentRef ||
      !code ||
      ["COMPLETED", "SUCCESS"].includes(paymentStatus) ||
      paymentStatus === "FAILED"
    ) {
      return;
    }

    const interval = setInterval(() => {
      void checkStatusNow();
    }, 3000);

    return () => clearInterval(interval);
  }, [paymentRef, code, paymentStatus]);

  useEffect(() => {
    if (!code) {
      setLoading(false);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    apiRequest<ApiRecord>(
      "public",
      `/payment-links/${encodeURIComponent(code)}/checkout`,
      { authenticated: false, signal: ctrl.signal },
    )
      .then(({ data: payload }) => setData(record(payload)))
      .catch((e) => {
        if (!isAbort(e) && !ctrl.signal.aborted) setError(e);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, [code, tick]);

  const countries = useMemo<CheckoutCountry[]>(() => {
    const rawCountries = rows(data?.countries).map(record);
    if (rawCountries.length > 0) {
      return rawCountries.map((c) => {
        const cCode = text(c, "code") || "CM";
        const ops = rows(c.operators)
          .map(record)
          .map((op) => ({
            code: text(op, "code"),
            label: text(op, "label") || text(op, "code"),
            payment_method: text(op, "payment_method") || text(op, "code"),
            otp_required:
              op.otp_required === true || text(op, "otp_required") === "1",
          }))
          .filter(
            (operator) =>
              !isGenericPaymentMethod(operator.code) &&
              !isGenericPaymentMethod(operator.label),
          );
        const pms = rows(c.payment_methods)
          .map(record)
          .map((pm) => ({
            code: text(pm, "code"),
            label: text(pm, "label") || text(pm, "code"),
            otp_required:
              pm.otp_required === true || text(pm, "otp_required") === "1",
          }));
        return {
          code: cCode,
          name: countryOf(cCode).name || text(c, "name") || cCode,
          currency: text(c, "currency") || countryOf(cCode).currency || "XAF",
          payable_amount:
            text(c, "payable_amount") || String(pick(data, "amount") ?? "0"),
          exchange_rate: text(c, "exchange_rate") || "1",
          operators: ops,
          payment_methods: pms,
        };
      });
    }
    return [];
  }, [data]);

  useEffect(() => {
    if (
      countries.length > 0 &&
      !countries.some((country) => country.code === selectedCountry)
    ) {
      const defCode = text(data, "country") || countries[0].code;
      const found = countries.find((c) => c.code === defCode) ?? countries[0];
      setSelectedCountry(found.code);
    } else if (countries.length === 0) {
      setSelectedCountry("");
    }
  }, [countries, data, selectedCountry]);

  const activeCountry = useMemo(
    () =>
      countries.find((c) => c.code === selectedCountry) ?? countries[0] ?? null,
    [countries, selectedCountry],
  );

  const operators = useMemo<CheckoutOperator[]>(() => {
    return activeCountry?.operators ?? [];
  }, [activeCountry]);

  useEffect(() => {
    if (operators.length > 0) {
      const exists = operators.some((op) => op.code === selectedOperator);
      if (!exists) {
        setSelectedOperator(operators[0].code);
      }
    } else {
      setSelectedOperator("");
    }
  }, [operators, selectedOperator]);

  const dialPrefix = useMemo(() => {
    if (!activeCountry) return "+237";
    return countryOf(activeCountry.code).dial || "+237";
  }, [activeCountry]);

  const normalizedPhone = useMemo(() => {
    const cleaned = phone.replace(/[\s()-]/g, "");
    if (!cleaned) return "";
    if (cleaned.startsWith("+")) return cleaned;
    const digits = cleaned.replace(/^\+/, "");
    const prefixDigits = dialPrefix.replace(/^\+/, "");
    if (
      prefixDigits &&
      digits.startsWith(prefixDigits) &&
      digits.length >= 10
    ) {
      return `+${digits}`;
    }
    return `${dialPrefix}${digits}`;
  }, [phone, dialPrefix]);

  const activeOperatorObj = useMemo(
    () => operators.find((op) => op.code === selectedOperator) ?? operators[0],
    [operators, selectedOperator],
  );

  const resolvedPaymentMethod = useMemo(() => {
    return activeOperatorObj?.payment_method || "";
  }, [activeOperatorObj]);
  const otpRequired = activeOperatorObj?.otp_required === true;

  const isCustomAmount = Boolean(
    data?.allow_custom_amount || !data?.amount || Number(data?.amount) <= 0,
  );
  const [customAmount, setCustomAmount] = useState("");

  useEffect(() => {
    if (data && pick(data, "amount") && !customAmount) {
      const initAmt = String(pick(data, "amount"));
      if (Number(initAmt) > 0) {
        setCustomAmount(initAmt);
      }
    }
  }, [data, customAmount]);

  const isCameroonOrange =
    activeCountry?.code === "CM" &&
    (activeOperatorObj?.code?.toLowerCase().includes("orange") ||
      selectedOperator?.toLowerCase().includes("orange") ||
      resolvedPaymentMethod?.toLowerCase().includes("orange"));

  const effectivePayAmount = isCustomAmount
    ? Number(customAmount)
    : Number(pick(data, "amount") ?? 0);
  const isBelowMinOrange =
    isCameroonOrange && effectivePayAmount > 0 && effectivePayAmount < 300;

  const valid =
    /^\+[1-9]\d{7,14}$/.test(normalizedPhone) &&
    Boolean(selectedCountry) &&
    Boolean(resolvedPaymentMethod) &&
    (!otpRequired || Boolean(otpCode.trim())) &&
    (!isCustomAmount || Number(customAmount) > 0) &&
    !isBelowMinOrange;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    const payerEmail =
      email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
        ? email.trim().toLowerCase()
        : `payer.${normalizedPhone.replace(/\D/g, "")}@bizconnectpay.com`;

    try {
      const bodyPayload: Record<string, unknown> = {
        email: payerEmail,
        phone_number: normalizedPhone,
        country: activeCountry?.code,
        operator: (
          activeOperatorObj?.code ??
          selectedOperator ??
          ""
        ).toLowerCase(),
        payment_method: resolvedPaymentMethod,
      };
      if (otpRequired) bodyPayload.otp_code = otpCode.trim();
      if (isCustomAmount && customAmount) {
        bodyPayload.amount = customAmount;
      }

      const response = await apiRequest<ApiRecord>(
        "public",
        `/payment-links/${encodeURIComponent(code)}/checkout`,
        {
          authenticated: false,
          method: "POST",
          headers: { "Idempotency-Key": idempotencyKey() },
          body: bodyPayload,
        },
      );
      const createdPayment = record(response.data);
      setResult(createdPayment);
      setPaymentStatus(
        text(createdPayment, "status").toUpperCase() || "PROCESSING",
      );
      if (text(createdPayment, "provider_reference")) {
        setProviderRef(text(createdPayment, "provider_reference"));
      }
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  const linkDescription = text(data, "description");
  const rawMerchant = text(data, "merchant_name") || "";
  const isDefaultAdmin = /bizconnectpay\s+administration/i.test(rawMerchant);
  const merchantName = isDefaultAdmin ? "" : rawMerchant;
  const displayTitle = linkDescription || merchantName || "BizConnectPay";

  const linkAmount =
    isCustomAmount && customAmount ? customAmount : pick(data, "amount");
  const linkCurrency = text(data, "currency") || "XAF";
  const feePayer = text(data, "fee_payer").toUpperCase() || "MERCHANT";
  const feeAmount = pick(data, "fee_amount", "collection_fee_amount");
  const feePercentage = text(
    data,
    "fee_percentage",
    "collection_fee_percentage",
  );
  const payableAmount =
    (isCustomAmount && customAmount ? customAmount : null) ??
    activeCountry?.payable_amount ??
    pick(data, "payable_amount", "charged_amount", "amount");
  const payableCurrency = activeCountry?.currency || linkCurrency;
  const isCrossCurrency =
    Boolean(payableCurrency) &&
    Boolean(linkCurrency) &&
    payableCurrency !== linkCurrency;

  return (
    <main className="flex min-h-[100dvh] flex-col items-center justify-center bg-slate-100 px-4 py-8">
      <div className="mb-5 flex w-full max-w-md items-center justify-between">
        <Logo />
        <LanguageSwitcher variant="auth" />
      </div>

      <div className="w-full max-w-md space-y-5">
        {/* Card 1: Payment Information */}
        <section
          className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
          data-testid="checkout-card"
        >
          {/* Header displaying link description */}
          <div className="border-b border-slate-100 px-6 py-4 text-center">
            <h1 className="text-base font-bold tracking-wide text-slate-900">
              {displayTitle}
            </h1>
            {merchantName && linkDescription && (
              <p className="mt-0.5 text-xs text-slate-500 uppercase tracking-wider">
                {merchantName}
              </p>
            )}
          </div>

          <div className="p-6">
            {!code ? (
              <p className="text-sm text-slate-600">
                {t("checkout.invalidLink")}
              </p>
            ) : loading ? (
              <div className="space-y-3" aria-busy="true">
                <div className="h-12 animate-pulse rounded-xl bg-slate-100" />
                <div className="h-11 animate-pulse rounded-lg bg-slate-100" />
                <div className="h-11 animate-pulse rounded-lg bg-slate-100" />
                <div className="h-11 animate-pulse rounded-lg bg-slate-100" />
              </div>
            ) : error && !data ? (
              <ErrorBox
                error={error}
                onRetry={() => setTick((value) => value + 1)}
              />
            ) : result ? (
              <div className="py-4 text-center" data-testid="checkout-success">
                {["COMPLETED", "SUCCESS"].includes(paymentStatus) ? (
                  <>
                    <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 animate-in zoom-in-75">
                      <CheckCircle2 className="h-10 w-10" />
                    </span>
                    <h2 className="mt-4 text-xl font-bold text-slate-900">
                      Paiement validé avec succès !
                    </h2>
                    <p className="mt-1 text-sm text-slate-600">
                      Votre transaction a été confirmée et traitée.
                    </p>
                    <div className="mt-5 text-left">
                      <ReceiptTicket
                        transaction={result}
                        amount={result.amount ?? payableAmount}
                        currency={text(result, "currency") || payableCurrency}
                        reference={paymentRef}
                        operation="Dépôt"
                        paymentMethod={
                          activeOperatorObj?.label ?? resolvedPaymentMethod
                        }
                        phone={normalizedPhone}
                        country={activeCountry?.name}
                        merchant={merchantName || undefined}
                      />
                    </div>
                    {text(result, "success_url") && (
                      <Button
                        className="mt-4"
                        variant="outline"
                        onClick={() => {
                          window.location.href = text(result, "success_url");
                        }}
                      >
                        Retourner à la boutique
                      </Button>
                    )}
                  </>
                ) : paymentStatus === "FAILED" ? (
                  <>
                    <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-rose-100 text-rose-700">
                      <ShieldAlert className="h-10 w-10" />
                    </span>
                    <h2 className="mt-4 text-xl font-bold text-slate-900">
                      Le paiement n'a pas abouti
                    </h2>
                    <p className="mt-2 text-sm text-slate-600">
                      La transaction a été rejetée ou a expiré sur votre
                      téléphone.
                    </p>
                    <div className="mt-4 flex justify-center gap-3">
                      <Button
                        variant="default"
                        onClick={() => {
                          setResult(null);
                          setPaymentStatus("PROCESSING");
                          setError(null);
                        }}
                      >
                        Réessayer le paiement
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                      <Loader2 className="h-8 w-8 animate-spin" />
                    </span>
                    <h2 className="mt-4 text-xl font-bold text-slate-900">
                      {t("checkout.requestSent")}
                    </h2>
                    <p className="mt-2 text-sm text-slate-600">
                      {t("checkout.validateOnPhone", {
                        phone: normalizedPhone,
                      })}
                    </p>
                    <div className="mt-4 rounded-xl bg-slate-100 px-4 py-3 text-left space-y-1.5 font-mono text-xs text-slate-800">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Référence:</span>
                        <span className="font-bold">{paymentRef}</span>
                      </div>
                      {providerRef && (
                        <div className="flex justify-between text-emerald-800">
                          <span>Réf Fournisseur:</span>
                          <span className="font-bold">{providerRef}</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-4 flex items-center justify-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pollingBusy}
                        onClick={() => void checkStatusNow()}
                        className="gap-2 text-xs"
                      >
                        {pollingBusy ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RotateCw className="h-3.5 w-3.5" />
                        )}
                        Actualiser le statut
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <form className="space-y-4" onSubmit={submit}>
                <h2 className="text-base font-bold text-slate-900">
                  {t("checkout.paymentInfo")}
                </h2>

                {/* Montant libre / Custom Amount */}
                {isCustomAmount && (
                  <div className="grid gap-1.5">
                    <Label
                      htmlFor="checkout-amount-input"
                      className="text-sm font-medium text-slate-700"
                    >
                      {t("checkout.amount") || "Montant à payer"}
                    </Label>
                    <div className="relative">
                      <Input
                        id="checkout-amount-input"
                        type="number"
                        min={1}
                        step="any"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        placeholder="Entrez le montant"
                        className="h-11 rounded-lg border-slate-300 bg-white pr-16 text-base font-bold"
                        data-testid="input-checkout-custom-amount"
                        required
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-500 uppercase">
                        {payableCurrency}
                      </span>
                    </div>
                    <span className="text-[11px] text-slate-500">
                      Montant libre (Donation / Pourboire)
                    </span>
                  </div>
                )}

                {/* Country Selection */}
                <div className="grid gap-1.5">
                  <Label
                    htmlFor="checkout-country"
                    className="text-sm font-medium text-slate-700"
                  >
                    {t("checkout.country")}
                  </Label>
                  <Select
                    value={selectedCountry}
                    onValueChange={(val) => {
                      setSelectedCountry(val);
                      setSelectedOperator("");
                      setOtpCode("");
                    }}
                  >
                    <SelectTrigger
                      id="checkout-country"
                      data-testid="select-checkout-country"
                      className="h-11 rounded-lg border-slate-300 bg-white"
                    >
                      <SelectValue placeholder={t("checkout.selectCountry")} />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          <span className="flex items-center gap-2.5">
                            <Flag code={c.code} />
                            <span>{c.name}</span>
                            <span className="text-xs text-slate-400">
                              ({c.currency})
                            </span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Operator Selection */}
                <div className="grid gap-1.5">
                  <Label
                    htmlFor="checkout-operator"
                    className="text-sm font-medium text-slate-700"
                  >
                    {t("checkout.operator")}
                  </Label>
                  <Select
                    value={selectedOperator}
                    onValueChange={(value) => {
                      setSelectedOperator(value);
                      setOtpCode("");
                    }}
                    disabled={!selectedCountry || operators.length === 0}
                  >
                    <SelectTrigger
                      id="checkout-operator"
                      data-testid="select-checkout-operator"
                      className="h-11 rounded-lg border-slate-300 bg-white"
                    >
                      <SelectValue placeholder={t("checkout.selectOperator")} />
                    </SelectTrigger>
                    <SelectContent>
                      {operators.map((op) => (
                        <SelectItem key={op.code} value={op.code}>
                          {op.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {otpRequired && (
                  <div className="grid gap-1.5">
                    <Label htmlFor="checkout-otp">
                      {t("checkout.otpCode")}
                    </Label>
                    <Input
                      id="checkout-otp"
                      data-testid="input-checkout-otp"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={32}
                      className="h-11 rounded-lg border-slate-300 bg-white"
                      value={otpCode}
                      onChange={(event) => setOtpCode(event.target.value)}
                      placeholder={t("checkout.otpPlaceholder")}
                      required
                    />
                    <p className="text-xs text-slate-500">
                      {t("checkout.otpHelp")}
                    </p>
                  </div>
                )}

                {/* Phone Number with Country Flag & Prefix */}
                <div className="grid gap-1.5">
                  <Label
                    htmlFor="checkout-phone"
                    className="text-sm font-medium text-slate-700"
                  >
                    {t("checkout.phone")}
                  </Label>
                  <div className="flex items-center overflow-hidden rounded-lg border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-emerald-500">
                    <div className="flex h-11 items-center gap-1.5 border-r border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-700">
                      {activeCountry && <Flag code={activeCountry.code} />}
                      <span>{dialPrefix}</span>
                    </div>
                    <Input
                      id="checkout-phone"
                      type="tel"
                      autoComplete="tel"
                      inputMode="tel"
                      className="h-11 border-0 shadow-none focus-visible:ring-0"
                      value={phone}
                      onChange={(event) => setPhone(event.target.value)}
                      placeholder={t("checkout.phonePlaceholder")}
                      required
                    />
                  </div>
                </div>

                {/* Optional Email */}
                <div className="grid gap-1.5">
                  <Label
                    htmlFor="checkout-email"
                    className="text-xs font-medium text-slate-500"
                  >
                    {t("checkout.email")}
                  </Label>
                  <Input
                    id="checkout-email"
                    type="email"
                    autoComplete="email"
                    className="h-10 rounded-lg border-slate-200 text-sm"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder={t("checkout.emailPlaceholder")}
                  />
                </div>

                {Boolean(error) && <ErrorBox error={error} />}

                {/* Green Pay Button */}
                <Button
                  className="h-12 w-full rounded-lg bg-emerald-500 text-base font-bold text-white shadow-sm hover:bg-emerald-600 disabled:opacity-60"
                  type="submit"
                  disabled={!valid || busy}
                  data-testid="button-submit-checkout"
                >
                  {busy ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("checkout.processing")}
                    </>
                  ) : (
                    `${t("checkout.pay")} ${fmtAmount(payableAmount, payableCurrency)}`
                  )}
                </Button>

                {/* Simple and secure payment box */}
                <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50/70 p-4 text-center">
                  <p className="text-xs font-semibold text-slate-700">
                    {t("checkout.simpleAndSecure")}
                  </p>
                  <div className="mt-2.5 flex flex-wrap items-center justify-center gap-4 text-xs text-slate-600">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white">
                        <Check className="h-2.5 w-2.5" />
                      </span>
                      {t("checkout.secure100")}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white">
                        <Check className="h-2.5 w-2.5" />
                      </span>
                      {t("checkout.support247")}
                    </span>
                  </div>
                </div>
              </form>
            )}
          </div>
        </section>

        {/* Card 2: Order Summary */}
        {data && (
          <section
            className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
            data-testid="checkout-order-summary"
          >
            <h2 className="text-base font-bold text-slate-900">
              {t("checkout.orderSummary")}
            </h2>
            <div className="mt-4 flex items-start gap-3.5">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-red-600 text-white shadow-xs">
                <Store className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-slate-900">
                  {displayTitle}
                </p>
                {merchantName && linkDescription && (
                  <p className="mt-0.5 text-xs text-slate-500 uppercase tracking-wider">
                    {merchantName}
                  </p>
                )}
                {text(data, "expires_at") && (
                  <p className="mt-1 text-[11px] text-slate-400">
                    {t("checkout.validUntil", {
                      date: fmtWhen(pick(data, "expires_at")),
                    })}
                  </p>
                )}
              </div>
            </div>

            <div className="my-4 border-t border-slate-100" />

            {feePayer === "CUSTOMER" &&
              !isCrossCurrency &&
              Number(feeAmount) > 0 && (
                <div className="mb-3 space-y-1.5 text-xs text-slate-600">
                  <div className="flex items-center justify-between">
                    <span>{t("checkout.amount")}</span>
                    <span className="font-mono font-semibold">
                      {fmtAmount(linkAmount, linkCurrency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>
                      {t("checkout.processingFee")}
                      {feePercentage ? ` (${feePercentage}%)` : ""}
                    </span>
                    <span className="font-mono font-semibold">
                      + {fmtAmount(feeAmount, linkCurrency)}
                    </span>
                  </div>
                </div>
              )}

            <div className="flex items-baseline justify-between">
              <span className="text-sm font-bold text-slate-800">
                {t("checkout.total")}
              </span>
              <div className="text-right">
                <span className="text-lg font-extrabold text-slate-900">
                  {fmtAmount(payableAmount, payableCurrency)}
                </span>
                {isCrossCurrency && (
                  <p className="text-[11px] text-slate-500">
                    {t("checkout.merchantAmountNotice", {
                      amount: fmtAmount(linkAmount, linkCurrency),
                    })}
                  </p>
                )}
              </div>
            </div>
          </section>
        )}
      </div>

      <p className="mt-6 flex items-center gap-1.5 text-xs text-slate-500">
        <ShieldCheck className="h-3.5 w-3.5" /> {t("checkout.securedBy")}
      </p>
    </main>
  );
}
